// Checkout form component for collecting customer information
import React, { useState, useEffect, useMemo } from 'react';
import { Customer, OrderFormData, OpeningHoursStatus, PaymentMethod } from '../types';
import { useCartStore } from '../store/cartStore';
import { roundCurrency } from '../shared/pricing';
import { lookupPostcode, normalizePostcode, isPostcodeComplete, PostcodeLookupResult } from '../shared/postcodes';
import { addDaysLondon, londonDateKey, londonDayOfWeek, nowLondon } from '../shared/dateUtils';
import './CheckoutForm.css';

// Time slot configuration - must match backend SLOT_DURATION_MINUTES
const SLOT_DURATION_MINUTES = 20;

interface CheckoutFormProps {
    onSubmit: (orderData: OrderFormData) => void;
    onCancel: () => void;
    isSubmitting: boolean;
    error: string | null;
    initialData?: Customer | null;
}

type FieldErrorKey = keyof Customer | 'paymentMethod' | 'deliveryDate' | 'deliveryTime';
type FieldErrors = Partial<Record<FieldErrorKey, string>>;

const CheckoutForm: React.FC<CheckoutFormProps> = ({
    onSubmit,
    onCancel,
    isSubmitting,
    error,
    initialData
}) => {
    const [formData, setFormData] = useState<Customer>({
        name: initialData?.name || '',
        email: initialData?.email || '',
        phone: initialData?.phone || '',
        address: initialData?.address || '',
        // New structured address fields
        postcode: initialData?.postcode || '',
        number: initialData?.number || '',
        street: initialData?.street || '',
        area: initialData?.area || '',
        isPostcodeFound: initialData?.isPostcodeFound,
    });

    // Postcode input state - defaults to "CB23 " if no saved postcode
    const getInitialPostcodeInput = () => {
        if (initialData?.postcode) {
            // Reconstruct formatted postcode from normalized version
            const normalized = initialData.postcode;
            if (normalized.length === 7) {
                return normalized.slice(0, 4) + ' ' + normalized.slice(4);
            }
            return normalized;
        }
        // Always show default "CB23 " if no saved postcode
        return 'CB23 ';
    };
    const [postcodeInput, setPostcodeInput] = useState<string>(getInitialPostcodeInput());
    const [numberInput, setNumberInput] = useState<string>(initialData?.number || '');
    const [postcodeLookupResult, setPostcodeLookupResult] = useState<PostcodeLookupResult | null>(null);

    // Postcode lookup effect - triggers when postcode input has 7+ non-space chars
    useEffect(() => {
        if (isPostcodeComplete(postcodeInput)) {
            const result = lookupPostcode(postcodeInput);
            setPostcodeLookupResult(result);

            // Update formData with lookup results
            const normalizedPostcode = normalizePostcode(postcodeInput);
            setFormData(prev => ({
                ...prev,
                postcode: normalizedPostcode,
                street: result.found && result.entry ? result.entry.description : '',
                area: result.found && result.entry ? result.entry.area : '',
                isPostcodeFound: result.found,
            }));
        } else {
            setPostcodeLookupResult(null);
            setFormData(prev => ({
                ...prev,
                postcode: '',
                street: '',
                area: '',
                isPostcodeFound: undefined,
            }));
        }
    }, [postcodeInput]);

    // Update formData.number when numberInput changes
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            number: numberInput,
        }));
    }, [numberInput]);

    // Computed: Combined address display (readonly third field)
    const combinedAddressDisplay = useMemo(() => {
        if (!postcodeLookupResult?.found || !postcodeLookupResult.entry) {
            return '';
        }
        const parts = [
            numberInput.trim(),
            postcodeLookupResult.entry.description,
            postcodeLookupResult.entry.area,
            postcodeLookupResult.entry.postcode,
        ].filter(Boolean);
        return parts.join(', ');
    }, [numberInput, postcodeLookupResult]);

    // Computed: Dynamic label for number field (shows "Number & Street" when postcode not found)
    const numberFieldLabel = useMemo(() => {
        if (isPostcodeComplete(postcodeInput) && postcodeLookupResult && !postcodeLookupResult.found) {
            return '🔢 Number & Street *';
        }
        return '🔢 Number *';
    }, [postcodeInput, postcodeLookupResult]);

    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [stockValidationError, setStockValidationError] = useState<string | null>(null);
    const [openingStatus, setOpeningStatus] = useState<OpeningHoursStatus | null>(null); const [deliveryDate, setDeliveryDate] = useState<string>('');
    const [deliveryTime, setDeliveryTime] = useState<string>('');
    const [deliveryNotes, setDeliveryNotes] = useState<string>('');
    const [availableTimes, setAvailableTimes] = useState<string[]>([]);
    const [slotAvailability, setSlotAvailability] = useState<Record<string, boolean>>({});
    const [hasOpenTimesInNext8Days, setHasOpenTimesInNext8Days] = useState<boolean>(true);
    const [availableDates, setAvailableDates] = useState<Array<{ date: string; dayName: string }>>([]);
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(undefined);
    const [driverTipAdded, setDriverTipAdded] = useState<boolean>(false);
    const tipOptions = [1, 2, 3, 5, 10];
    const [driverTipAmount, setDriverTipAmount] = useState<number>(tipOptions[1]);
    const [receiveEmailUpdates, setReceiveEmailUpdates] = useState<boolean>(true);
    const { validateAndCorrectCart, cart } = useCartStore();
    const effectiveDriverTipAmount = paymentMethod === 'cash_on_delivery' || !driverTipAdded ? 0 : driverTipAmount;
    const baseSubtotal = roundCurrency(cart.totals.subtotal);
    const discountAmount = roundCurrency(cart.totals.discountAmount);
    const discountedSubtotal = roundCurrency(cart.totals.total);
    const orderTotalWithTip = roundCurrency(discountedSubtotal + effectiveDriverTipAmount);

    // // Read Apple Pay eligibility from global store (populated by OrderPage)
    // const {
    //     isEligible: isApplePayEligible,
    //     isChecked,
    //     isChecking,
    //     error: applePayError,
    //     supportMode: applePaySupportMode,
    //     crossDeviceFallbackAvailable: applePayFallbackAvailable
    // } = useApplePayStore();

    // console.log('[CheckoutForm] Apple Pay store state:', {
    //     isApplePayEligible,
    //     isChecked,
    //     isChecking,
    //     applePayError,
    //     applePaySupportMode,
    //     applePayFallbackAvailable
    // });

    // const showApplePayOption = isApplePayEligible || applePaySupportMode === 'cross-device';
    // const applePayHelperMessage = applePaySupportMode === 'cross-device'
    //     ? 'Use your iPhone or other Apple device to scan a secure code after checkout.'
    //     : null;
    // const applePayUnavailableMessage = applePaySupportMode === 'unsupported' ? applePayError : null;

    // Set payment method to cash_on_delivery for free orders
    useEffect(() => {
        if (discountedSubtotal === 0 && paymentMethod !== 'cash_on_delivery') {
            setPaymentMethod('cash_on_delivery');
        }
    }, [discountedSubtotal]);

    // Fetch opening hours status on component mount
    useEffect(() => {
        fetchOpeningStatus();
    }, []);

    const fetchOpeningStatus = async () => {
        try {
            const response = await fetch('/api/opening-hours/status');
            if (response.ok) {
                const status = await response.json() as OpeningHoursStatus;
                setOpeningStatus(status);

                // Check if store has any open times in the next 8 days
                await checkOpenTimesInNext8Days();

                // If store is closed, set delivery date to next opening time
                if (!status.isOpen && status.nextOpeningTime) {
                    // Server already returns a London wall-clock date (`YYYY-MM-DD`).
                    // Slicing keeps backward-compat in case it ever sends an ISO string.
                    const dateStr = status.nextOpeningTime.date.slice(0, 10);
                    setDeliveryDate(dateStr);
                    setDeliveryTime(status.nextOpeningTime.time);
                    generateAvailableTimes(dateStr);
                }
            }
        } catch (error) {
            console.error('Failed to fetch opening status:', error);
        }
    };

    const checkOpenTimesInNext8Days = async () => {
        try {
            const response = await fetch('/api/opening-hours');
            if (response.ok) {
                const openingHours = await response.json() as any;
                // Evaluate "today" and "now" in Europe/London — never use the
                // browser's local timezone or UTC.
                const london = nowLondon();
                const todayKey = london.date;
                let hasOpenTime = false;
                const openDates: Array<{ date: string; dayName: string }> = [];

                const dayNumberToName: Record<number, string> = {
                    0: 'sunday',
                    1: 'monday',
                    2: 'tuesday',
                    3: 'wednesday',
                    4: 'thursday',
                    5: 'friday',
                    6: 'saturday'
                };

                const dayNumberToDisplayName: Record<number, string> = {
                    0: 'Sunday',
                    1: 'Monday',
                    2: 'Tuesday',
                    3: 'Wednesday',
                    4: 'Thursday',
                    5: 'Friday',
                    6: 'Saturday'
                };

                // Check each day in the next 8 days
                console.log('🔄 Checking next 8 days for open times...');
                const currentTime = london.minutes; // Current London minutes since midnight

                for (let i = 0; i < 8; i++) {
                    const dateStr = addDaysLondon(todayKey, i);
                    const dow = londonDayOfWeek(dateStr);
                    const dayName = dayNumberToName[dow];
                    const displayDayName = dayNumberToDisplayName[dow];

                    // Check exceptions first
                    const exception = openingHours.exceptions?.find((ex: any) => ex.date === dateStr);
                    const schedule = exception || openingHours.weeklySchedule?.find((day: any) => day.dayOfWeek === dayName);

                    console.log(`  Day ${i}: ${dateStr} (${displayDayName}/${dayName})`, {
                        foundSchedule: !!schedule,
                        isOpen: schedule?.isOpen,
                        hasSlots: schedule?.timeSlots?.length > 0
                    });

                    if (schedule?.isOpen && schedule.timeSlots && schedule.timeSlots.length > 0) {
                        // For today (i === 0), check if there are any remaining time slots
                        let hasRemainingSlots = true;
                        if (i === 0) {
                            hasRemainingSlots = schedule.timeSlots.some((slot: any) => {
                                let endTime = timeToMinutes(slot.end);
                                // Handle midnight as end of day
                                if (endTime === 0 || endTime < timeToMinutes(slot.start)) {
                                    endTime = 24 * 60;
                                }
                                return endTime > currentTime;
                            });
                            console.log(`    Today check: currentTime=${currentTime}, hasRemainingSlots=${hasRemainingSlots}`);
                        }

                        if (hasRemainingSlots) {
                            hasOpenTime = true;
                            openDates.push({
                                date: dateStr,
                                dayName: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : displayDayName
                            });
                        }
                    }
                }
                console.log('✅ Open dates found:', openDates);

                setHasOpenTimesInNext8Days(hasOpenTime);
                setAvailableDates(openDates);
            }
        } catch (error) {
            console.error('Failed to check open times:', error);
            setHasOpenTimesInNext8Days(true); // Default to true on error
            setAvailableDates([]);
        }
    };

    const generateAvailableTimes = async (date: string) => {
        try {
            const response = await fetch('/api/opening-hours');
            if (response.ok) {
                const openingHours = await response.json() as any;
                // `date` is a London wall-clock date key `YYYY-MM-DD`.
                // Look up its day-of-week in London (handles dates that fall
                // on a different day in UTC vs London during DST edge cases).
                const dow = londonDayOfWeek(date);

                const dayNumberToName: Record<number, string> = {
                    0: 'sunday',
                    1: 'monday',
                    2: 'tuesday',
                    3: 'wednesday',
                    4: 'thursday',
                    5: 'friday',
                    6: 'saturday'
                };
                const dayName = dayNumberToName[dow];

                console.log('🔍 generateAvailableTimes:', {
                    inputDate: date,
                    londonDow: dow,
                    dayName: dayName
                });

                // Find schedule for selected date (check exceptions first)
                const exception = openingHours.exceptions?.find((ex: any) => ex.date === date);
                const schedule = exception || openingHours.weeklySchedule?.find((day: any) => day.dayOfWeek === dayName);

                console.log('📅 Schedule lookup:', {
                    lookingForDate: date,
                    lookingForDayName: dayName,
                    hasException: !!exception,
                    foundSchedule: !!schedule,
                    scheduleDay: schedule?.dayOfWeek,
                    isOpen: schedule?.isOpen,
                    timeSlots: schedule?.timeSlots,
                    allWeeklySchedule: openingHours.weeklySchedule?.map((d: any) => ({ day: d.dayOfWeek, open: d.isOpen }))
                });

                if (schedule?.isOpen && schedule.timeSlots) {
                    const times: string[] = [];

                    // Check if this is today - if so, filter out past times
                    const londonNow = nowLondon();
                    const isToday = date === londonNow.date;
                    const currentTimeMinutes = isToday ? londonNow.minutes : 0;

                    schedule.timeSlots.forEach((slot: any) => {
                        const rawStart = timeToMinutes(slot.start);
                        let end = timeToMinutes(slot.end);

                        // Round up start time to next slot boundary
                        // e.g., 09:04 (544 mins) -> 09:20 (560 mins) for 20-min slots
                        const start = Math.ceil(rawStart / SLOT_DURATION_MINUTES) * SLOT_DURATION_MINUTES;

                        // Handle midnight (00:00) as end of day (24:00 = 1440 minutes)
                        if (end === 0 || end <= rawStart) {
                            end = 24 * 60; // 1440 minutes = midnight end of day
                        }

                        console.log(`  Generating times for slot ${slot.start}-${slot.end}:`, {
                            rawStart,
                            start,
                            end,
                            isToday,
                            currentTimeMinutes,
                            willGenerate: end > start
                        });

                        // Generate time slot intervals
                        for (let time = start; time < end; time += SLOT_DURATION_MINUTES) {
                            // Skip past times if this is today
                            if (isToday && time <= currentTimeMinutes) {
                                continue;
                            }

                            const hours = Math.floor(time / 60) % 24; // Wrap hours at 24
                            const minutes = time % 60;
                            times.push(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
                        }
                    });
                    console.log(`  ✅ Generated ${times.length} time slots`);
                    setAvailableTimes(times);

                    // Fetch slot availability for this date
                    if (times.length > 0) {
                        try {
                            const slotsResponse = await fetch(`/api/slots/${date}?times=${times.join(',')}`);
                            if (slotsResponse.ok) {
                                const slotsData = await slotsResponse.json() as {
                                    slots: Array<{ time: string; isAvailable: boolean }>
                                };
                                const availability: Record<string, boolean> = {};
                                slotsData.slots.forEach((slot: { time: string; isAvailable: boolean }) => {
                                    availability[slot.time] = slot.isAvailable;
                                });
                                setSlotAvailability(availability);
                            }
                        } catch (slotError) {
                            console.error('Failed to fetch slot availability:', slotError);
                            // Default all slots to available on error
                            const defaultAvailability: Record<string, boolean> = {};
                            times.forEach(time => { defaultAvailability[time] = true; });
                            setSlotAvailability(defaultAvailability);
                        }
                    }
                } else {
                    console.log('  ❌ No schedule or not open');
                    setAvailableTimes([]);
                    setSlotAvailability({});
                }
            }
        } catch (error) {
            console.error('Failed to generate available times:', error);
            setAvailableTimes([]);
            setSlotAvailability({});
        }
    };

    const timeToMinutes = (time: string): number => {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    };

    const handleDeliveryDateChange = async (date: string) => {
        setDeliveryDate(date);
        setDeliveryTime('');
        setAvailableTimes([]); // Reset times immediately
        setSlotAvailability({}); // Reset slot availability
        clearFieldError('deliveryDate');
        clearFieldError('deliveryTime');
        if (date) {
            await generateAvailableTimes(date);
            // After generating times, check if date is valid
            // This will be caught by generateAvailableTimes setting empty array for closed days
        }
    };

    const handleDeliveryTimeChange = (timeValue: string) => {
        setDeliveryTime(timeValue);
        clearFieldError('deliveryTime');
    };

    const clearFieldError = (key: FieldErrorKey) => {
        setFieldErrors(prev => {
            if (!prev[key]) {
                return prev;
            }
            const { [key]: _removed, ...rest } = prev;
            return rest;
        });
    };

    const validateForm = (): boolean => {
        const errors: FieldErrors = {};

        // Postcode is required
        if (!postcodeInput.trim()) {
            errors.postcode = 'Postcode is required';
        } else if (!isPostcodeComplete(postcodeInput)) {
            errors.postcode = 'Please enter a complete postcode (e.g., CB23 5AB)';
        }

        // Number is required
        if (!numberInput.trim()) {
            errors.number = 'Number is required';
        }

        // Name and phone are optional, but email is required

        const trimmedEmail = formData.email.trim();
        if (!trimmedEmail) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            errors.email = 'Please enter a valid email address';
        }

        // Phone format validation only if provided
        if (formData.phone.trim() && !/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
            errors.phone = 'Please enter a valid phone number';
        }

        // Payment method is required
        if (!paymentMethod) {
            errors.paymentMethod = 'Please select a payment option';
        }

        if (openingStatus && !openingStatus.isOpen) {
            if (!deliveryDate) {
                errors.deliveryDate = 'Please choose a delivery date';
            }

            if (!deliveryTime) {
                errors.deliveryTime = 'Please choose a delivery time';
            }
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    }; const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (validateForm()) {
            // Validate stock before submitting order
            setStockValidationError(null);

            try {
                const stockValidation = await validateAndCorrectCart();

                if (stockValidation && !stockValidation.isValid) {
                    setStockValidationError(
                        `Cart updated due to stock changes: ${stockValidation.message}. Please review your order and try again.`
                    );
                    return;
                }

                // Determine delivery date/time. The server is the source of
                // truth for UTC conversion — we send Europe/London wall-clock
                // components (`YYYY-MM-DD` and `HH:MM`) and let the worker
                // combine them via `londonWallClockToUTC` before persisting.
                const isImmediate = !deliveryDate || !deliveryTime;
                const submitDeliveryDate = isImmediate ? '' : deliveryDate;
                const submitDeliveryTime = isImmediate ? '' : deliveryTime;

                // [CLIENT_DISCOUNT_DEBUG] Log discount values being sent to server
                console.log('[CLIENT_DISCOUNT_DEBUG] Order submission - discount details:', {
                    discountCode: cart.discount?.code ?? null,
                    discountPercentage: cart.discount?.percentage ?? null,
                    discountAmount,
                    baseSubtotal,
                    discountedSubtotal,
                    cartTotals: cart.totals,
                    discountObject: cart.discount,
                    isStale: cart.discount?.isStale ?? false,
                    validatedSubtotal: cart.discount?.validatedSubtotal ?? null
                });

                const orderData: OrderFormData = {
                    customer: {
                        name: formData.name?.trim() || '',
                        email: formData.email?.trim() || '',
                        phone: formData.phone?.trim() || '',
                        address: formData.address?.trim() || '',  // Keep legacy field
                        // New structured address fields
                        postcode: formData.postcode || '',
                        number: formData.number || '',
                        street: formData.street || '',
                        area: formData.area || '',
                        isPostcodeFound: formData.isPostcodeFound,
                        receiveEmailUpdates
                    },
                    cart,
                    deliveryDate: submitDeliveryDate,
                    deliveryTime: submitDeliveryTime,
                    deliverNow: isImmediate,
                    deliveryNotes: deliveryNotes.trim() || undefined,
                    paymentMethod: paymentMethod!,
                    subtotal: baseSubtotal,
                    discountCode: cart.discount?.code ?? null,
                    discountPercentage: cart.discount?.percentage ?? null,
                    discountAmount,
                    driverTipAdded: paymentMethod === 'cash_on_delivery'
                        ? false
                        : driverTipAdded,
                    driverTipAmount: paymentMethod === 'cash_on_delivery' || !driverTipAdded
                        ? 0
                        : driverTipAmount
                };

                onSubmit(orderData);
            } catch (stockError) {
                setStockValidationError('Failed to validate stock. Please try again.');
            }
        }
    };

    const handleInputChange = (field: keyof Customer, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear field error when user starts typing
        clearFieldError(field);
    };

    const handlePaymentMethodChange = (method: PaymentMethod) => {
        setPaymentMethod(method);
        if (method === 'cash_on_delivery') {
            setDriverTipAdded(false);
        }
        clearFieldError('paymentMethod');
    };

    const handleDriverTipChange = (checked: boolean) => {
        setDriverTipAdded(checked);
    };

    const handleReceiveEmailUpdatesChange = (checked: boolean) => {
        setReceiveEmailUpdates(checked);
        if (!checked) {
            clearFieldError('email');
        }
    };

    return (
        <div className="checkout-form">
            <h3>Delivery Information</h3>

            <form onSubmit={handleSubmit}>

                <div className="form-group">
                    <label htmlFor="postcode">📮 Postcode *</label>
                    <input
                        type="text"
                        id="postcode"
                        value={postcodeInput}
                        onChange={(e) => {
                            setPostcodeInput(e.target.value.toUpperCase());
                            clearFieldError('postcode');
                        }}
                        className={fieldErrors.postcode ? 'error' : ''}
                        disabled={isSubmitting}
                        placeholder="CB23 5AB"
                        autoComplete="postal-code"
                    />
                    {fieldErrors.postcode && (
                        <span className="field-error">{fieldErrors.postcode}</span>
                    )}
                </div>

                <div className="form-group">
                    <label htmlFor="number">{numberFieldLabel}</label>
                    <input
                        type="text"
                        id="number"
                        value={numberInput}
                        onChange={(e) => {
                            setNumberInput(e.target.value);
                            clearFieldError('number');
                        }}
                        className={fieldErrors.number ? 'error' : ''}
                        disabled={isSubmitting}
                        placeholder="e.g., 15 or Flat 2, 15"
                    />
                    {fieldErrors.number && (
                        <span className="field-error">{fieldErrors.number}</span>
                    )}
                </div>

                {/* Combined address display - only shown when postcode is found */}
                {postcodeLookupResult?.found && (
                    <div className="form-group address-preview">
                        <label>🏠 Delivery Address</label>
                        <div className="address-display">
                            {combinedAddressDisplay || <span className="placeholder">Enter number above to see full address</span>}
                        </div>
                    </div>
                )}

                <div className="form-group">
                    <label htmlFor="name">👤 Name</label>
                    <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className={fieldErrors.name ? 'error' : ''}
                        disabled={isSubmitting}
                    />
                    {fieldErrors.name && (
                        <span className="field-error">{fieldErrors.name}</span>
                    )}
                </div>

                <div className="form-group">
                    <label htmlFor="email">✉️ Email *</label>
                    <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={fieldErrors.email ? 'error' : ''}
                        disabled={isSubmitting}
                    />
                    {fieldErrors.email && (
                        <span className="field-error">{fieldErrors.email}</span>
                    )}
                    <label className="email-updates-checkbox">
                        <input
                            type="checkbox"
                            checked={receiveEmailUpdates}
                            onChange={(e) => handleReceiveEmailUpdatesChange(e.target.checked)}
                            disabled={isSubmitting}
                        />
                        <span>Get occasional updates</span>
                    </label>
                </div>

                <div className="form-group">
                    <label htmlFor="phone">☎️ Phone</label>
                    <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={fieldErrors.phone ? 'error' : ''}
                        disabled={isSubmitting}
                        placeholder=""
                    />
                    {fieldErrors.phone && (
                        <span className="field-error">{fieldErrors.phone}</span>
                    )}
                </div>


                <div className="form-group">
                    <label htmlFor="deliveryNotes">📝 Notes</label>
                    <textarea
                        id="deliveryNotes"
                        value={deliveryNotes}
                        onChange={(e) => setDeliveryNotes(e.target.value)}
                        disabled={isSubmitting}
                        rows={2}
                        placeholder="Special instructions for delivery or order"
                    />
                </div>

                <div className="form-group">
                    <span className="payment-label">Pay By *</span>
                    <div className="payment-options">
                        {/* Free order - show only Free Order option */}
                        {discountedSubtotal === 0 ? (
                            <label className={`payment-option ${paymentMethod === 'cash_on_delivery' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value="cash_on_delivery"
                                    checked={paymentMethod === 'cash_on_delivery'}
                                    onChange={() => handlePaymentMethodChange('cash_on_delivery')}
                                    disabled={isSubmitting}
                                />
                                <div className="payment-content">
                                    <span className="payment-title">🎁 Free Order</span>
                                </div>
                            </label>
                        ) : (
                            <>
                                <label className={`payment-option ${paymentMethod === 'cash_on_delivery' ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="cash_on_delivery"
                                        checked={paymentMethod === 'cash_on_delivery'}
                                        onChange={() => handlePaymentMethodChange('cash_on_delivery')}
                                        disabled={isSubmitting}
                                    />
                                    <div className="payment-content">
                                        <span className="payment-title">💵 Cash</span>
                                    </div>
                                </label>
                                <label className={`payment-option ${paymentMethod === 'card_on_delivery' ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="card_on_delivery"
                                        checked={paymentMethod === 'card_on_delivery'}
                                        onChange={() => handlePaymentMethodChange('card_on_delivery')}
                                        disabled={isSubmitting}
                                    />
                                    <div className="payment-content">
                                        <span className="payment-title">💳 Card at door</span>
                                    </div>
                                </label>
                                <label className={`payment-option ${paymentMethod === 'card_online' ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="card_online"
                                        checked={paymentMethod === 'card_online'}
                                        onChange={() => handlePaymentMethodChange('card_online')}
                                        disabled={isSubmitting}
                                    />
                                    <div className="payment-content">
                                        <span className="payment-title">🌐 Online Payment Options</span>
                                    </div>
                                </label>
                            </>
                        )}

                    </div>
                    {fieldErrors.paymentMethod && (
                        <span className="field-error">{fieldErrors.paymentMethod}</span>
                    )}
                    {/* {applePayUnavailableMessage && (
                        <div className="payment-info payment-info--warning" role="status">
                            {applePayUnavailableMessage}
                        </div>
                    )} */}
                    {/* Hide driver tip for free orders (discountedSubtotal === 0) */}
                    {paymentMethod && paymentMethod !== 'cash_on_delivery' && discountedSubtotal > 0 && (
                        <>
                            <label className="email-updates-checkbox driver-tip-checkbox">
                                <input
                                    type="checkbox"
                                    checked={driverTipAdded}
                                    onChange={(e) => handleDriverTipChange(e.target.checked)}
                                    disabled={isSubmitting}
                                />
                                <span>❤️ Add a tip for rider?</span>
                            </label>
                            {driverTipAdded && (
                                <div className="driver-tip-chips" role="radiogroup" aria-label="Select a driver tip">
                                    {tipOptions.map((amount) => (
                                        <button
                                            key={amount}
                                            type="button"
                                            className={`driver-tip-chip ${driverTipAmount === amount ? 'selected' : ''}`}
                                            onClick={() => setDriverTipAmount(amount)}
                                            disabled={isSubmitting}
                                            aria-pressed={driverTipAmount === amount}
                                        >
                                            £{amount}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>


                {/* Delivery Time Selection - Show only if store is closed */}
                {openingStatus && !openingStatus.isOpen && (
                    <>
                        {!hasOpenTimesInNext8Days ? (
                            <div className="delivery-warning">
                                <h4>⚠️ Currently Unavailable</h4>
                                <p>We have no available delivery times in the next 8 days. Please check back later.</p>
                            </div>
                        ) : (
                            <>
                                <div className="delivery-time-info">
                                    <h4>🕐 Schedule Your Order</h4>
                                    <p>We're currently closed. Please select when you'd like to receive your order:</p>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="deliveryDate">Delivery Date *</label>

                                    {/* Date selection buttons - only show available dates */}
                                    {availableDates.length > 0 ? (
                                        <div className="date-selection-buttons">
                                            {availableDates.map((dateOption) => {
                                                // Parse date correctly to avoid timezone issues
                                                const [year, month, day] = dateOption.date.split('-').map(Number);
                                                const dateObj = new Date(year, month - 1, day);
                                                return (
                                                    <button
                                                        key={dateOption.date}
                                                        type="button"
                                                        className={`date-button ${deliveryDate === dateOption.date ? 'selected' : ''}`}
                                                        onClick={() => handleDeliveryDateChange(dateOption.date)}
                                                        disabled={isSubmitting}
                                                    >
                                                        <span className="date-day">{dateOption.dayName}</span>
                                                        <span className="date-value">{dateObj.getDate()}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="no-dates-available">No available dates in the next 7 days.</p>
                                    )}
                                    {fieldErrors.deliveryDate && (
                                        <span className="field-error">{fieldErrors.deliveryDate}</span>
                                    )}
                                </div>

                                {deliveryDate && availableTimes.length > 0 && (
                                    <div className="form-group">
                                        <label>Delivery Time *</label>
                                        <div className="time-selection-grid">
                                            {availableTimes.map(time => {
                                                const isAvailable = slotAvailability[time] !== false;
                                                return (
                                                    <button
                                                        key={time}
                                                        type="button"
                                                        className={`time-button ${deliveryTime === time ? 'selected' : ''} ${!isAvailable ? 'unavailable' : ''}`}
                                                        onClick={() => isAvailable && handleDeliveryTimeChange(time)}
                                                        disabled={isSubmitting || !isAvailable}
                                                        title={!isAvailable ? 'This time slot is fully booked' : `Select ${time}`}
                                                    >
                                                        {time}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {fieldErrors.deliveryTime && (
                                            <span className="field-error">{fieldErrors.deliveryTime}</span>
                                        )}
                                    </div>
                                )}

                                {deliveryDate && availableTimes.length === 0 && (
                                    <div className="delivery-warning">
                                        <p>⚠️ We're closed on the selected date. Please choose another date.</p>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {Object.keys(fieldErrors).length > 0 && (
                    <div className="form-errors-summary">
                        <h4>Please fix the following errors:</h4>
                        <ul>
                            {Object.entries(fieldErrors).map(([field, error]) => (
                                <li key={field}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {error && (
                    <div className="error-message" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                        {error}
                    </div>
                )}

                {stockValidationError && (
                    <div className="error-message" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                        {stockValidationError}
                    </div>
                )}

                <div className="form-actions">
                    <button
                        type="button"
                        className="cancel-btn"
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting
                            ? 'Placing Order...'
                            : paymentMethod === 'card_online'
                                ? 'Proceed to pay'
                                : openingStatus && !openingStatus.isOpen
                                    ? 'Schedule Order'
                                    : 'Place Order'}
                    </button>
                </div>

                <div className="checkout-total-summary" aria-live="polite">
                    <div className="summary-row">
                        <span className="summary-label">Subtotal</span>
                        <span className="summary-value">£{baseSubtotal.toFixed(2)}</span>
                    </div>
                    {discountAmount > 0 && (
                        <div className="summary-row discount">
                            <span className="summary-label">Discount</span>
                            <span className="summary-value">−£{discountAmount.toFixed(2)}</span>
                        </div>
                    )}
                    <div className="summary-row">
                        <span className="summary-label">Basket total</span>
                        <span className="summary-value">£{discountedSubtotal.toFixed(2)}</span>
                    </div>
                    {effectiveDriverTipAmount > 0 && (
                        <div className="summary-row tip">
                            <span className="summary-label">Rider tip</span>
                            <span className="summary-value">£{effectiveDriverTipAmount.toFixed(2)}</span>
                        </div>
                    )}
                    <div className="summary-row total">
                        <span className="summary-label">Total due</span>
                        <span className="summary-value">£{orderTotalWithTip.toFixed(2)}</span>
                    </div>
                </div>


            </form>
        </div>
    );
};

export default CheckoutForm;
